import { instance } from './instance'

/**
 * Hotel-Edition API Services
 *
 * Bündelt:
 *  - Locations (Outlets innerhalb einer Property)
 *  - Rooms + RoomTokens (HMAC-signierte QR-Codes)
 *  - PreOrders
 *  - Hotel-Statistiken
 *  - Onboarding (SuperAdmin)
 */

// ─── Locations ──────────────────────────────────────────
export const locationService = {
  list(params = {}) {
    return instance.get('/locations', { params }).then(r => r.data)
  },
  /** Öffentlich (Gast): Outlets ohne Login, z. B. für Vorbestellungen */
  listPublic(params = {}) {
    return instance.get('/customer/locations', { params }).then(r => r.data?.data ?? r.data ?? [])
  },
  get(id) {
    return instance.get(`/locations/${id}`).then(r => r.data?.data ?? r.data)
  },
  create(payload) {
    return instance.post('/locations', payload).then(r => r.data?.data ?? r.data)
  },
  update(id, payload) {
    return instance.put(`/locations/${id}`, payload).then(r => r.data?.data ?? r.data)
  },
  remove(id) {
    return instance.delete(`/locations/${id}`).then(r => r.data)
  },
  // ─ Outlet-spezifisches Menü ─
  getMenu(locationId) {
    return instance.get(`/locations/${locationId}/menu`).then(r => r.data)
  },
  updateMenu(locationId, payload) {
    return instance.put(`/locations/${locationId}/menu`, payload).then(r => r.data)
  },
  resetMenu(locationId) {
    return instance.put(`/locations/${locationId}/menu`, { reset: true }).then(r => r.data)
  },
  copyMenuFrom(locationId, sourceId) {
    return instance.post(`/locations/${locationId}/menu/copy-from/${sourceId}`).then(r => r.data)
  },
}

// ─── Rooms ──────────────────────────────────────────────
export const roomService = {
  list(params = {}) {
    return instance.get('/rooms', { params }).then(r => r.data)
  },
  get(id) {
    return instance.get(`/rooms/${id}`).then(r => r.data?.data ?? r.data)
  },
  create(payload) {
    return instance.post('/rooms', payload).then(r => r.data)
  },
  bulkCreate(payload) {
    return instance.post('/rooms/bulk', payload).then(r => r.data)
  },
  update(id, payload) {
    return instance.put(`/rooms/${id}`, payload).then(r => r.data?.data ?? r.data)
  },
  remove(id) {
    return instance.delete(`/rooms/${id}`).then(r => r.data)
  },
  checkIn(id, payload) {
    return instance.post(`/rooms/${id}/check-in`, payload).then(r => r.data)
  },
  checkOut(id) {
    return instance.post(`/rooms/${id}/check-out`).then(r => r.data)
  },
}

// ─── Room-Tokens ────────────────────────────────────────
export const roomTokenService = {
  issue(roomId, payload) {
    return instance.post(`/rooms/${roomId}/tokens`, payload).then(r => r.data)
  },
  rotate(roomId) {
    return instance.post(`/rooms/${roomId}/tokens/rotate`).then(r => r.data)
  },
  revoke(tokenId) {
    return instance.delete(`/room-tokens/${tokenId}`).then(r => r.data)
  },
  validatePublic(token) {
    return instance.post('/room-tokens/validate', { token }).then(r => r.data?.data ?? r.data)
  },
  /**
   * Speichert den vom Gast eingegebenen Nachnamen am Zimmer / Token, damit beim
   * nächsten Scan der Name automatisch erkannt wird.
   */
  attachGuestLastname(token, lastname) {
    return instance.post('/room-tokens/attach-guest', { token, lastname }).then(r => r.data)
  },
}

// ─── PreOrders ──────────────────────────────────────────
export const preOrderService = {
  list(params = {}) {
    return instance.get('/pre-orders', { params }).then(r => r.data)
  },
  get(id) {
    return instance.get(`/pre-orders/${id}`).then(r => r.data?.data ?? r.data)
  },
  create(payload) {
    return instance.post('/pre-orders', payload).then(r => r.data?.data ?? r.data)
  },
  updateStatus(id, status) {
    return instance.patch(`/pre-orders/${id}/status`, { status }).then(r => r.data?.data ?? r.data)
  },
  promote(id) {
    return instance.post(`/pre-orders/${id}/promote`).then(r => r.data?.data ?? r.data)
  },
}

// ─── Statistiken ────────────────────────────────────────
export const hotelStatsService = {
  overview(days = 30) {
    return instance.get('/hotel-stats/overview', { params: { days } }).then(r => r.data)
  },
  groupOverview(days = 30) {
    return instance.get('/hotel-stats/group-overview', { params: { days } }).then(r => r.data)
  },
  liveOccupancy(restaurantId) {
    return instance.get('/live-occupancy', { params: { restaurant_id: restaurantId } }).then(r => r.data)
  },
}

// ─── Voucher ────────────────────────────────────────────
export const voucherService = {
  list(params = {}) {
    return instance.get('/vouchers', { params }).then(r => r.data)
  },
  create(payload) {
    return instance.post('/vouchers', payload).then(r => r.data?.data ?? r.data)
  },
  validate(code, subtotal, locationId = null) {
    return instance.post('/vouchers/validate', {
      code,
      subtotal,
      location_id: locationId,
    }).then(r => r.data)
  },
}

// ─── Split-Bill ─────────────────────────────────────────
export const splitBillService = {
  show(sessionId) {
    return instance.get(`/order-sessions/${sessionId}/split`).then(r => r.data)
  },
  create(sessionId, payload) {
    return instance.post(`/order-sessions/${sessionId}/split`, payload).then(r => r.data)
  },
  markPaid(sessionId, folioToken, paymentMethod) {
    return instance.post(`/order-sessions/${sessionId}/split/pay`, {
      folio_token: folioToken,
      payment_method: paymentMethod,
    }).then(r => r.data)
  },
}

// ─── Waiter-App ─────────────────────────────────────────
export const waiterService = {
  tables(locationId = null) {
    const params = locationId ? { location_id: locationId } : {}
    return instance.get('/waiter/tables', { params }).then(r => r.data)
  },
  detail(tableId) {
    return instance.get(`/waiter/tables/${tableId}`).then(r => r.data)
  },
  take(tableId) {
    return instance.post(`/waiter/tables/${tableId}/take`).then(r => r.data)
  },
  release(tableId, force = false) {
    return instance.post(`/waiter/tables/${tableId}/release`, { force }).then(r => r.data)
  },
  markServed(orderId) {
    return instance.post(`/waiter/orders/${orderId}/served`).then(r => r.data)
  },
  acknowledgeCall(sessionId) {
    return instance.post(`/waiter/sessions/${sessionId}/acknowledge-call`).then(r => r.data)
  },
  payTab(sessionId, paymentMethod) {
    return instance.post(`/waiter/sessions/${sessionId}/pay-tab`, { payment_method: paymentMethod }).then(r => r.data)
  },
}

// ─── Bulk-Edit Tables + Rooms ───────────────────────────
export const bulkService = {
  // Tische
  bulkCreateTables(payload) {
    return instance.post('/tables/bulk', payload).then(r => r.data)
  },
  bulkUpdateTables(ids, changes) {
    return instance.patch('/tables/bulk', { ids, changes }).then(r => r.data)
  },
  bulkDeleteTables(ids) {
    return instance.delete('/tables/bulk', { data: { ids } }).then(r => r.data)
  },
  // Zimmer
  bulkUpdateRooms(ids, changes) {
    return instance.patch('/rooms/bulk', { ids, changes }).then(r => r.data)
  },
  bulkDeleteRooms(ids) {
    return instance.delete('/rooms/bulk', { data: { ids } }).then(r => r.data)
  },
}

// ─── Konzernzentrale (hotel_group_admin / superadmin) ───
export const hotelGroupService = {
  properties() {
    return instance.get('/hotel-group/properties').then(r => r.data?.data ?? r.data ?? [])
  },
  users(params = {}) {
    return instance.get('/hotel-group/users', { params }).then(r => r.data?.data ?? r.data ?? [])
  },
  createUser(payload) {
    return instance.post('/hotel-group/users', payload).then(r => r.data)
  },
  updateUser(id, payload) {
    return instance.put(`/hotel-group/users/${id}`, payload).then(r => r.data)
  },
  removeUser(id) {
    return instance.delete(`/hotel-group/users/${id}`).then(r => r.data)
  },
}

// ─── Onboarding (SuperAdmin) ────────────────────────────
export const onboardingService = {
  customers() {
    return instance.get('/onboarding/customers').then(r => r.data?.data ?? r.data)
  },
  create(payload) {
    return instance.post('/onboarding', payload).then(r => r.data)
  },
}
